import { SEO } from "@/components/SEO";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Check, Droplets, Zap, Shield, TrendingUp } from "lucide-react";

const Germany = () => {
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "European Markets", url: "/european-markets" },
    { name: "Germany", url: "/markets/germany" }
  ];

  const features = [
    {
      icon: <Droplets className="w-6 h-6" />,
      title: "Wassersparend Technology",
      description: "Advanced water-saving systems reducing consumption by up to 70% while maintaining optimal performance"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Precision Engineering",
      description: "German-standard precision components with tolerances measured in micrometers for perfect operation"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "TÜV Certified Quality",
      description: "All systems certified to meet rigorous German safety and quality standards"
    }
  ];

  return (
    <>
      <SEO
        title="Smart-Toiletten & Designer-Badewannen | Aureum VII"
        description="Premium-Smart-Toiletten REM7, freistehende Solid-Surface-Badewannen ARIA und Whirlpool-Spas für Bauträger und Hotels in Deutschland."
        keywords="smart WC Germany, wassersparend toilet, German intelligent toilet, smart toilet Deutschland, WC intelligent Germany, precision toilet engineering, German bathroom technology"
        breadcrumbs={breadcrumbs}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Smart WC Germany",
          "description": "Premium intelligent toilet systems engineered for German market",
          "url": "https://www.aureum7trading.com/markets/germany"
        }}
      />
      <Navigation />
      
      <main className="min-h-screen bg-gradient-to-b from-background to-background/95">
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
                  <span className="text-2xl">🇩🇪</span>
                  <span className="text-sm font-medium">German Engineering</span>
                </div>
                
                <h1 className="text-5xl md:text-6xl font-bold mb-6">
                  Smart WC Germany
                </h1>
                
                <p className="text-xl text-muted-foreground mb-8">
                  Präzise deutsche Ingenieurskunst trifft auf intelligente Sanitärtechnologie. 
                  Wassersparend, zuverlässig, und technisch überlegen.
                </p>

                <div className="flex items-center gap-2 px-6 py-3 bg-card rounded-lg border mb-8 inline-flex">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <span className="font-semibold">4,000-6,000</span>
                  <span className="text-muted-foreground">monthly searches</span>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg">
                    <LocalizedLink to="/contact">Anfrage stellen</LocalizedLink>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <LocalizedLink to="/smart-toilets">Produktkollektion</LocalizedLink>
                  </Button>
                </div>
              </div>

              <div className="relative">
                <Card className="p-8">
                  <h3 className="text-2xl font-bold mb-4">Technische Spezifikationen</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <span>Wasserverbrauch: 2.5L pro Spülung (70% Einsparung)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <span>Energieeffizienz: A++ Rating mit Smart-Standby</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <span>Präzisionskeramik: ±0.05mm Toleranz</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <span>TÜV-geprüfte Sicherheitssysteme</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <span>10 Jahre Garantie auf Kernkomponenten</span>
                    </li>
                  </ul>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 px-4 bg-card/30">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Deutsche Ingenieursqualität
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Technical Excellence Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-center mb-12">
              Warum Smart WC für den deutschen Markt?
            </h2>
            
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p className="mb-6">
                Der deutsche Markt verlangt höchste technische Standards und Präzision. 
                Unsere intelligenten WC-Systeme erfüllen diese Anforderungen durch:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-card p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-foreground mb-3">Wassersparende Technologie</h3>
                  <p>
                    Modernste Wasserspartechnik reduziert den Verbrauch um bis zu 70% 
                    im Vergleich zu herkömmlichen WC-Anlagen, ohne Kompromisse bei der Leistung.
                  </p>
                </div>
                
                <div className="bg-card p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-foreground mb-3">Präzisionsfertigung</h3>
                  <p>
                    Jede Komponente wird mit deutscher Präzision gefertigt. 
                    Toleranzen im Mikrometerbereich garantieren jahrzehntelangen, störungsfreien Betrieb.
                  </p>
                </div>
                
                <div className="bg-card p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-foreground mb-3">Umfassende Dokumentation</h3>
                  <p>
                    Vollständige technische Spezifikationen, Installationsanleitungen und 
                    Wartungspläne in deutscher Sprache verfügbar.
                  </p>
                </div>
                
                <div className="bg-card p-6 rounded-lg border">
                  <h3 className="text-xl font-semibold text-foreground mb-3">Lokaler Support</h3>
                  <p>
                    Deutschsprachiger technischer Support mit Verständnis für lokale 
                    Bauvorschriften und Installationsstandards.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Bereit für deutsche Ingenieursqualität?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Kontaktieren Sie unser deutsches Team für detaillierte technische Spezifikationen
            </p>
            <Button asChild size="lg">
              <LocalizedLink to="/contact">Jetzt Kontakt aufnehmen</LocalizedLink>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default Germany;